//node modules
import * as yup from "yup";
import React, {ChangeEvent, useCallback, useEffect, useMemo, useRef, useState} from "react";
import * as lodash from "lodash";
import {ActionMeta, MultiValue, SingleValue} from "react-select";
import {promiseResolve} from "../../../../reusable/utils/promises";
import moment from "moment/moment";
import ToJsDate from "to-js-date";

//validators
export interface ReactSelectOption {
  label: string;
  value: any;
}


export function useValidateForm<T extends any, U extends yup.AnyObjectSchema = never>(
  schema: U | yup.BaseSchema,
  initial: T,
  callback?: () => void
) {
  const [errors, setErrors] = useState<{ [key: string]: string[] }>({});
  const [isValidating, setIsValidating] = useState(false);
  const [formValues, setFormValues] = useState<T>(initial);

  const hasSetInitial = useRef(false);
  const initialValue = useRef<T>();

  useEffect(() => {
    if (!hasSetInitial.current) {
      initialValue.current = initial;
      hasSetInitial.current = true;
    }
  }, [initial]);


  const handleFormSubmit = (successCallback: (arg0: U extends object ? yup.InferType<U> : T) => Promise<void> | void, failCallback?: (() => Promise<void> | void)) => {
    if (!isValidating) setIsValidating(true);

    const {promise, resolve, reject} = promiseResolve<boolean, boolean>()
    schema
      .validate(formValues, {abortEarly: false})
      .then(async (value) => {
        console.log("successfully validated")
        setErrors({});
        await successCallback(value);
        if (callback) callback();
        resolve(true);
      })
      .catch(async (err: yup.ValidationError) => {
        if (err.name === "ValidationError") {
          let firstError: string | undefined;

          const errs = err.inner.reduce((errors: ({ [key: string]: string[] }), e) => {
            if (e.path) {
              errors = {
                ...errors,
                [e.path]: e.errors
              };
            }
            if (!firstError && e.errors.length) {
              firstError = e.path;
            }
            return errors;
          }, {});

          if (firstError) {
            const elems = document.getElementsByName(firstError);
            let found = false;

            for (let i = 0; i < elems.length && !found; i++) {
              const elem = elems[i];

              if (["input", "select", "checkbox", "textarea"].includes(elem.nodeName.toLowerCase())) {
                elem.focus();
              }
            }
          }

          console.log(errs);
          setErrors(errs);

          if (failCallback) {
            await failCallback()
          }

          reject(false);
        }
      });

    return promise;
  };


  const reValidate = useCallback(
    () => {
      if (isValidating) {
        try {
          schema.validateSync(formValues, {abortEarly: false});
          setErrors({});
          return true;
        } catch (err: any) {
          if (err.name === "ValidationError") {
            const errs = err.inner.reduce((errors: ({ [key: string]: string[] }), e: yup.ValidationError) => {
              if (e.path) {
                errors = {
                  ...errors,
                  [e.path]: e.errors
                };
              }
              return errors;
            }, {});
            setErrors(errs);
          }
        }
      }
    },
    [isValidating, formValues, schema],
  );

  const handleFieldChange = useMemo(() => {
    return ({target: {name, value}}: ChangeEvent<HTMLInputElement>) => {
      setFormValues((old: any) => {
        const newFormValues = old;
        lodash.set(newFormValues, name?.split("."), value);

        return {...newFormValues}
      });
    }
  }, [setFormValues]);

  const handleCheckboxChange = useMemo(() => {
    return ({target: {name, checked}}: ChangeEvent<HTMLInputElement>) => {
      setFormValues((old: any) => {
        const newFormValues = old;
        lodash.set(newFormValues, name?.split("."), checked);

        return {...newFormValues}
      });
    }
  }, [setFormValues]);

  const handlePhoneInput = useMemo(() => {
    return (value: string, data: any, ccField: string, nField: string) => {
      const countryCode = lodash.has(data, "dialCode") ? data?.dialCode : "";
      const number = lodash.has(data, "dialCode") ? value.slice(data.dialCode.length) : "";

      setFormValues((old: any) => {
        const newFormValues = old;
        lodash.set(newFormValues, ccField?.split("."), countryCode);
        lodash.set(newFormValues, nField?.split("."), number);

        return {...newFormValues};
      });
    }
  }, [setFormValues]);

  const getPhoneValue = useMemo(() => {
    return (ccField: string, nField: string) => {
      const countryCode = lodash.get(formValues, ccField?.split(".")) as string;
      const number = lodash.get(formValues, nField?.split(".")) as string;

      if (!countryCode || !number) {
        return null;
      }

      return countryCode + number;
    }
  }, [formValues]);

  const handleDateChange = useMemo(() => {
    return ({target: {name, value}}: React.ChangeEvent<HTMLInputElement>) => {
      setFormValues((old: any) => {
        const newFormValues = old;

        lodash.set(newFormValues, name?.split("."), moment(ToJsDate(value)).tz('Europe/Oslo').toDate());

        return {...newFormValues}
      });
    }
  }, [setFormValues]);

  const handleSelectChange = useMemo(() => {
    return (value: SingleValue<ReactSelectOption> | SingleValue<ReactSelectOption>[], field: string) => {
      setFormValues((old: any) => {
        const newFormValues = old;

        if (Array.isArray(value)) {
          lodash.set(newFormValues, field?.split("."), value.reduce((output: SingleValue<ReactSelectOption>[], value) => {
            if (value !== null) {
              output.push(value?.value);
            }

            return output;
          }, []));
        } else if (value !== null) {
          lodash.set(newFormValues, field?.split("."), value.value);
        }

        return {...newFormValues};
      });
    }
  }, [setFormValues]);


  const getSelectOptions = useCallback((options: SingleValue<ReactSelectOption>[], value: any) => {
      return getSelectOption(options, value);
    },
    [],
  );

  const validateBankAccountNR = (bank_account: string) => {
    const weights = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2]

    if (bank_account?.length === 11) {
      const checkDigit = parseInt(bank_account.charAt(10), 10)
      const withoutCheckDigit = bank_account.substring(0, 10)
      let sum = 0
      for (let i = 0; i < 10; i++) {
        sum += parseInt(withoutCheckDigit.charAt(i), 10) * weights[i]
      }
      const remainder = sum % 11
      return checkDigit === (remainder === 0 ? 0 : 11 - remainder)
    }
  }

  const useCustomSelectOptions = useMemo(() => {
    return (
      original: ReactSelectOption[] | undefined,
      addedCustom: ReactSelectOption[] | undefined,
      setAddedCustom: React.Dispatch<React.SetStateAction<ReactSelectOption[]>> | React.Dispatch<React.SetStateAction<ReactSelectOption[] | undefined>>,
      newCustom: ReactSelectOption | undefined,
      setNewCustom: React.Dispatch<React.SetStateAction<ReactSelectOption | undefined>>,
      managerID?: string,
      bannedTags?: string[],
    ) => {

      const ignoredTags = bannedTags || [];
      if (managerID) {
        ignoredTags.unshift("manager");
      }

      const options = newCustom ? [...(original || []), ...(addedCustom || []), newCustom] : [...(original || []), ...(addedCustom || [])];
      if (managerID) {
        options.unshift({label: "Manager", value: managerID});
      }

      const handleCustomValue = (value: string) => {
        setNewCustom({label: value, value: value});
      }

      const addCustomToList = (value: string) => {
        if (value !== undefined && !getSelectOptions([...(original || []), ...(addedCustom || [])], value)) {
          setAddedCustom((old: any) => [...old, newCustom]);
          setNewCustom(undefined);
        }
      }

      const handleChange = (value: SingleValue<any> | MultiValue<any>, actionMeta: ActionMeta<SingleValue<ReactSelectOption> | undefined>, field: string) => {
        let validAction = !ignoredTags?.length;
        if (!validAction) {
          validAction = !["select-option", "remove-value", "pop-value"].includes(actionMeta.action);
        }
        if (!validAction) {
          validAction = !!actionMeta.option && !ignoredTags?.includes(actionMeta.option?.label?.toLowerCase());
        }
        if (!validAction) {
          validAction = !!actionMeta.removedValue && !ignoredTags?.includes(actionMeta.removedValue?.label?.toLowerCase())
        }

        if (validAction) {
          handleSelectChange(value, field);

          if (actionMeta.action === "select-option") {
            addCustomToList(actionMeta.option?.value);
          }
        }
      }


      return {options, handleCustomValue, handleChange, addCustomToList}
    }
  }, [getSelectOptions, handleSelectChange])


  const resetValidation = useCallback(
    () => {
      setIsValidating(false);
      setErrors({});
    },
    [],
  );

  const resetValues = useCallback(() => {
    if (initialValue.current) {
      setFormValues(initialValue.current);
      resetValidation();
    }
  }, [resetValidation]);


  return {
    formValues,
    setFormValues,
    errors,
    reValidate,
    handleFormSubmit,
    resetValidation,
    handleFieldChange,
    handlePhoneInput,
    handleSelectChange,
    handleCheckboxChange,
    handleDateChange,
    resetValues,
    getSelectOptions,
    getPhoneValue,
    validateBankAccountNR,
    useCustomSelectOptions,
  };
}

export function getSelectOption(options: SingleValue<ReactSelectOption>[], value: any): SingleValue<ReactSelectOption> | MultiValue<ReactSelectOption> | undefined {
  if (Array.isArray(value)) {
    return value.map((value) => {
      return options.find((o) => o?.value === value);
    }) as MultiValue<ReactSelectOption>;
  } else {
    return options.find((option) => option?.value === value);
  }
}
